import { Box, Button, Flex } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { AuthEvent } from 'enums/auth';
import useLogout from 'hooks/useLogout';
import { t } from 'i18next';
import { IFullDialog } from 'interfaces/i-dialogs';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { LanguageCode } from 'lib_ts/enums/translation';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuthStore } from 'stores/use-auth-store';
import CONTENT_EN from './content-en.md';
import CONTENT_JA from './content-ja.md';
import CONTENT_KO from './content-ko.md';

const COMPONENT_NAME = 'SafetyPolicyDialog';

interface IProps {
  show: boolean;
  onClose: (changed: boolean) => void;
  logoutFn: (reason: AuthEvent, silent?: boolean) => void;
}

export const SafetyPolicyDialog = (props: IProps) => {
  const { logout } = useLogout();
  const current = useAuthStore((s) => s.current);

  const [content, setContent] = useState('Please wait...');
  const [loggedOut, setLoggedOut] = useState(false);

  useEffect(() => {
    const c = (() => {
      switch (current.language) {
        case LanguageCode.Japanese: {
          return CONTENT_JA;
        }

        case LanguageCode.Korean: {
          return CONTENT_KO;
        }

        case LanguageCode.English:
        default: {
          return CONTENT_EN;
        }
      }
    })();

    fetch(c)
      .then((res) => res.text())
      .then((txt) => setContent(txt));
  }, [current.language]);

  if (loggedOut) {
    return null;
  }

  const DEFAULT_PROPS: IFullDialog = {
    identifier: COMPONENT_NAME,
    width: RADIX.DIALOG.WIDTH.XL,
    vFlexHeight: RADIX.DIALOG.HEIGHT.LG,
    title: 'common.safety-policy',
    content: (
      <Flex direction="column" gap={RADIX.FLEX.GAP.SECTION} pr="2">
        <Box className={RADIX.VFLEX.GROW}>
          <ReactMarkdown children={content} />
        </Box>
        <Flex gap={RADIX.FLEX.GAP.SM} justify="end">
          <Button
            color={RADIX.COLOR.SUCCESS}
            onClick={() => props.onClose(true)}
          >
            {t('common.accept')}
          </Button>

          <Button
            color={RADIX.COLOR.DANGER}
            onClick={() => {
              setLoggedOut(true);
              logout({
                reason: 'logout',
              });
            }}
          >
            {t('main.logout')}
          </Button>
        </Flex>
      </Flex>
    ),
    onClose: () => props.onClose(false),
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog {...DEFAULT_PROPS} />
    </ErrorBoundary>
  );
};
